import { ContentfulClient } from "@lululemon/ecom-pattern-library";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
import Cookies from "js-cookie";
import {
  trackEvent,
  EVENT_CATEGORY,
  EVENT_TYPE,
} from "@lululemon/mwa-analytics";
import { braQuizLogger } from "@lululemon/ecom-common/src/util/bra-quiz-logger";

import {
  BRA_QUIZ_LOCALSTORAGE_KEY,
  SHOW_BRA_RESULTS_MODAL_LOCALSTORAGE_KEY,
} from "./constants";

export const getFlattedContextData = (contextData) => {
  if (!Object.keys(contextData).length) {
    throw new Error("Missing context data for bra quiz components");
  }

  const {
    questions: {
      questions,
      general: {
        previousStep,
        nextStep,
        seeMyResults,
        paginationText,
        braQuizResultsAnalyticsId,
      },
    },
    fields,
  } = contextData;

  return {
    questions,
    fields,
    backButtonText: previousStep,
    nextButtonText: nextStep,
    seeMyResultsButtonText: seeMyResults,
    paginationText,
    braQuizResultsAnalyticsId,
  };
};

export const saveBraQuizResultsToLocalStorage = (braQuizSizesObj) => {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.setItem(
      BRA_QUIZ_LOCALSTORAGE_KEY,
      JSON.stringify(braQuizSizesObj)
    );
    localStorage.setItem(SHOW_BRA_RESULTS_MODAL_LOCALSTORAGE_KEY, true);
  } catch (error) {
    braQuizLogger({
      error,
      componentName: "packages/ecom-story/src/components/quiz/utils",
      functionName: "saveBraQuizResultsToLocalStorage",
      scopeContexts: {
        Results: braQuizSizesObj,
        "Local storage": { key: BRA_QUIZ_LOCALSTORAGE_KEY },
      },
    });
  }
};

export const removeBraQuizResultsFromLocalStorage = () => {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(BRA_QUIZ_LOCALSTORAGE_KEY);
    localStorage.removeItem(SHOW_BRA_RESULTS_MODAL_LOCALSTORAGE_KEY);
  } catch (error) {
    braQuizLogger({
      error,
      componentName: "packages/ecom-story/src/components/quiz/utils",
      functionName: "removeBraQuizResultsFromLocalStorage",
      scopeContexts: {
        "Local storage": {
          "Bra quiz key": BRA_QUIZ_LOCALSTORAGE_KEY,
          "Show results modal key": SHOW_BRA_RESULTS_MODAL_LOCALSTORAGE_KEY,
        },
      },
    });
  }
};

export const formatRequestBody = (questions, answers) => {
  const answersBody = questions.map((question, index) => {
    const fields = question.fields.map((field) => ({
      name: field,
      answer: answers[field].toString(),
    }));

    return {
      questionId: index + 1,
      fields,
    };
  });

  return { answers: answersBody };
};

const createContentfulClient = (locale) => {
  const { accessToken, environmentId, previewToken, spaceId } =
    publicConfig?.contentful || {};

  return new ContentfulClient({
    accessToken,
    environmentId,
    previewToken,
    spaceId,
    locale: locale || (Cookies.get("UsrLocale") || "").replace("_", "-"),
    isPreviewEnabled: false,
  });
};

export function getBraQuizData(locale = "") {
  const contentfulClient = createContentfulClient(locale);
  return contentfulClient.getFilteredEntries({
    contentType: "braQuiz",
    depth: 10,
  });
}

export const translatePills = (translations) => (text) => {
  if (translations && Object.entries(translations).length) {
    const dict = new Map(Object.entries(translations));
    if (/\w+\s\d+$/.test(text)) {
      const [size, number] = text.split(" ");

      return dict.has(size.toLowerCase())
        ? `${dict.get(size.toLowerCase())} ${number}`
        : text;
    }

    return dict.get(text.toLowerCase().trim().replace(" ", "_")) || text;
  }

  return text;
};

export const trackBraQuizPDP = (
  eventSubType,
  { id, type, text, attributes, ...rest }
) =>
  trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
    eventSubType,
    component: {
      id,
      type: type || "",
      text: text || "",
      position: -1,
      totalCount: -1,
      attributes: attributes || {},
      ...rest,
    },
  });

export const trackBqPDPInteraction = (componentData) =>
  trackBraQuizPDP(EVENT_TYPE.INTERACTION, componentData);

export const trackBqPDPImpression = (componentData) =>
  trackBraQuizPDP(EVENT_TYPE.IMPRESSION, componentData);

export const trackBQError = ({
  type = "validation",
  details = "",
  isGuestFacing = false,
  message = "",
}) =>
  trackEvent(EVENT_CATEGORY.ERROR, {
    error: {
      errorType: type,
      errorDetails: details,
      errorGuestFacing: isGuestFacing,
      errorMessage: message,
    },
  });
